import { TrackPoint } from '../contracts/vessel.types';

/**
 * COVERAGE GAPS — when is the line between two position reports a path, and
 * when is it a lie?
 *
 * A track is a list of position REPORTS, not a route. Two consecutive reports
 * are evidence of two places the vessel was; the line between them is an
 * assertion about a journey nobody observed. That assertion is safe when the
 * reports are seconds apart and false when they are days apart — and the
 * renderer used to draw both identically, because nothing in it read
 * `timeIso`. The result was a solid confident line from Kochi to Chennai,
 * 1,056 km straight over Tamil Nadu, for a vessel that had simply gone dark
 * while rounding the subcontinent.
 *
 * TWO TESTS, because either alone lets the wrong picture through:
 *
 *  - TIME. Beyond `TRACK_GAP_HOURS` we do not know what happened, whatever the
 *    distance covered. This is the test that catches the case above: 1,056 km
 *    over four days implies 5.9 kn, which is a perfectly ordinary speed — a
 *    speed test passes it without complaint.
 *  - IMPLIED SPEED. Below the time limit a leg can still be impossible: no
 *    merchant hull makes `TRACK_MAX_KNOTS`, so a "one hour, 300 km" pair is
 *    two different vessels, a bad fix, or a duplicated MMSI.
 *
 * Both are exported constants rather than literals because they are judgement
 * calls on real data, not physics. If genuine ocean tracks come back too
 * fragmented, `TRACK_GAP_HOURS` is the single number to raise.
 *
 * WHY THIS IS SHARED while the track CONTROLLER is not (D7 amendment,
 * 2026-08-13). Everything here is a rule about what AIS position reports mean —
 * true of any provider's feed, and the two consumers already prove it: Voyage
 * Radar's transport reads it to hold the ghost still across a dark period, and
 * its layer code reads it to lift the ink. What is provider-specific about
 * track replay — the price, the offered spans, the endpoint — is none of this.
 *
 * Pure model file: no `@angular/*`, no `leaflet` (rule R1). Its spec needs no
 * TestBed, no map and no Leaflet, which is the test that it belongs here.
 */

export const TRACK_GAP_HOURS = 6;
export const TRACK_MAX_KNOTS = 40;

const EARTH_RADIUS_NM = 3440.065;

/** Great-circle distance in nautical miles — the unit knots are defined in. */
export function trackDistanceNm(a: TrackPoint, b: TrackPoint): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_NM * Math.asin(Math.min(1, Math.sqrt(s)));
}

/**
 * Is the leg from `a` to `b` unobserved — a gap rather than a path?
 *
 * An UNREADABLE timestamp counts as a gap. The alternative is to assume
 * continuity from data we could not parse, which is the assumption this whole
 * mechanism exists to stop making. Out-of-order or identical stamps are not
 * gaps: `dtMs <= 0` means the two reports describe the same instant, so there
 * is no interval to have lost coverage over — and the speed test would divide
 * by zero on them.
 */
export function isTrackGap(a: TrackPoint, b: TrackPoint): boolean {
  const t0 = new Date(a?.timeIso ?? '').getTime();
  const t1 = new Date(b?.timeIso ?? '').getTime();
  if (!isFinite(t0) || !isFinite(t1)) return true;
  const dtMs = t1 - t0;
  if (dtMs > TRACK_GAP_HOURS * 3600 * 1000) return true;
  if (dtMs <= 0) return false;
  return trackDistanceNm(a, b) / (dtMs / 3600000) > TRACK_MAX_KNOTS;
}

/**
 * Split a track into runs of CONTINUOUS coverage.
 *
 * Every point appears exactly once and order is preserved, so the segments
 * concatenated are the input — the split decides only where the ink lifts.
 * A single point is a legitimate segment: a hull seen once between two long
 * silences is a fact, and dropping it would be a second kind of lying.
 */
export function splitTrackAtGaps(points: TrackPoint[]): TrackPoint[][] {
  const list = Array.isArray(points) ? points : [];
  if (!list.length) return [];
  const out: TrackPoint[][] = [[list[0]]];
  for (let i = 1; i < list.length; i++) {
    if (isTrackGap(list[i - 1], list[i])) out.push([list[i]]);
    else out[out.length - 1].push(list[i]);
  }
  return out;
}

/**
 * The unobserved legs, as endpoint pairs — what the faint connector draws.
 *
 * Derived from the same predicate as the split, so the two can never disagree
 * about where a gap is.
 */
export function trackGapLinks(points: TrackPoint[]): Array<[TrackPoint, TrackPoint]> {
  const list = Array.isArray(points) ? points : [];
  const out: Array<[TrackPoint, TrackPoint]> = [];
  for (let i = 1; i < list.length; i++) {
    if (isTrackGap(list[i - 1], list[i])) out.push([list[i - 1], list[i]]);
  }
  return out;
}

/**
 * "4 d 3 h" — how long contact was lost, for the gap marker's tooltip.
 *
 * Coarse on purpose, and coarser the longer it ran: minutes matter for a
 * six-hour dropout and are noise across four days. Returns null when the
 * interval cannot be read, which is the one case where the marker says only
 * that contact broke and refuses to put a number on it.
 */
export function trackGapLabel(a: TrackPoint, b: TrackPoint): string | null {
  const t0 = new Date(a?.timeIso ?? '').getTime();
  const t1 = new Date(b?.timeIso ?? '').getTime();
  if (!isFinite(t0) || !isFinite(t1) || t1 <= t0) return null;
  const mins = Math.round((t1 - t0) / 60000);
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) {
    const rem = mins % 60;
    return rem ? `${hours} h ${rem} min` : `${hours} h`;
  }
  const days = Math.floor(hours / 24);
  const rem = hours % 24;
  return rem ? `${days} d ${rem} h` : `${days} d`;
}
